// ============================================================
// Student Store (Zustand)
// ============================================================

import { create } from 'zustand';
import studentService from '@/services/studentService';

const useStudentStore = create((set, get) => ({
  students: [],
  currentStudent: null,
  dashboardStats: null,
  
  // Pagination State
  pagination: {
    page: 1,
    pageSize: 20,
    total: 0,
    totalPages: 1
  },
  
  // UI State
  loading: false,
  error: null,
  loadedSchoolId: null,

  // Fetch paginated students
  fetchStudents: async (schoolId, page = 1, pageSize = 20, forceRefetch = false, filters = {}) => {
    const state = get();
    // Cache check - omit if filters are provided to ensure accurate server-side filtering
    const hasFilters = Object.values(filters).some(val => val !== '' && val !== null && val !== undefined);
    if (!forceRefetch && !hasFilters && state.loadedSchoolId === schoolId && state.pagination.page === page && state.pagination.pageSize === pageSize && state.students.length > 0) {
      return;
    }

    set({ loading: true, error: null });
    try {
      const res = await studentService.getStudents(schoolId, page, pageSize, filters);
      set({ 
        students: res.items || [], 
        pagination: {
          page: res.page || 1,
          pageSize: Math.min(res.page_size || 20, 100),
          total: res.total || 0,
          totalPages: res.total_pages || 1
        },
        loadedSchoolId: schoolId, 
        loading: false 
      });
    } catch (err) {
      set({ error: err.message, loading: false });
    }
  },

  // Fetch single student
  fetchStudentById: async (schoolId, studentId) => {
    set({ loading: true, error: null, currentStudent: null });
    try {
      const res = await studentService.getStudentById(schoolId, studentId);
      set({ currentStudent: res, loading: false }); // res is the student object directly or under data depending on axios setup, assuming directly since no {data} wrapper in response in cURL
    } catch (err) {
      set({ error: err.message, loading: false });
    }
  },

  fetchDashboardStats: async (schoolId, sessionId) => {
    if (!schoolId) return;
    set({ loading: true, error: null });
    try {
      const res = await studentService.getDashboardStats(schoolId, sessionId);
      set({ dashboardStats: res, loading: false, error: null });
    } catch (err) {
      set({ error: err.message, loading: false });
    }
  },

  // Create student
  createStudent: async (schoolId, data) => {
    set({ loading: true, error: null });
    try {
      const res = await studentService.createStudent(schoolId, data);
      set({ loading: false, loadedSchoolId: null });
      return res;
    } catch (err) {
      set({ error: err.message, loading: false });
      throw err;
    }
  },

  // Update student
  updateStudent: async (schoolId, studentId, data) => {
    set({ loading: true, error: null });
    try {
      const res = await studentService.updateStudent(schoolId, studentId, data);
      set({ loading: false, loadedSchoolId: null });
      return res;
    } catch (err) {
      set({ error: err.message, loading: false });
      throw err;
    }
  },
  // Update student status
  updateStudentStatus: async (schoolId, studentId, status) => {
    set({ loading: true, error: null });
    try {
      const res = await studentService.updateStudentStatus(schoolId, studentId, status);
      const current = get().currentStudent;
      if (current) {
        set({
          currentStudent: {
            ...current,
            student: {
              ...(current.student || {}),
              status: status,
            },
            enrollment: {
              ...(current.enrollment || {}),
              status: status,
            }
          },
          loadedSchoolId: null,
          loading: false
        });
      } else {
        set({ loadedSchoolId: null, loading: false });
      }
      return res;
    } catch (err) {
      set({ error: err.message, loading: false });
      throw err;
    }
  },
  // Bulk update student status
  bulkUpdateStudentStatus: async (schoolId, studentIds, status) => {
    set({ loading: true, error: null });
    try {
      const res = await studentService.bulkUpdateStudentStatus(schoolId, studentIds, status);
      set({ loadedSchoolId: null, loading: false });
      return res;
    } catch (err) {
      set({ error: err.message, loading: false });
      throw err;
    }
  },

  // Delete student (Soft Delete)
  deleteStudent: async (schoolId, studentId) => {
    set({ loading: true, error: null });
    try {
      await studentService.deleteStudent(schoolId, studentId);
      // We could re-fetch or optimistically update
      set((state) => ({
        students: state.students.map((s) => (s.student_id === studentId ? { ...s, status: 'INACTIVE' } : s)),
        loading: false,
      }));
      return { success: true };
    } catch (err) {
      set({ error: err.message, loading: false });
      throw err;
    }
  },
  clearCurrentStudent: () => set({ currentStudent: null }),

  // Promote Students
  promoteStudents: async (schoolId, payload) => {
    set({ loading: true, error: null });
    try {
      const res = await studentService.promoteStudents(schoolId, payload);
      set({ loading: false });
      return res;
    } catch (err) {
      set({ error: err.message, loading: false });
      throw err;
    }
  },
}));

export default useStudentStore;
